import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// ---------- Guest magic-link exchange (pre-React) ----------
// The Guest Portal receives a magic link `/guests?t=TOKEN`. We swap that for a
// long-lived session token BEFORE React mounts, so we sidestep React 18
// StrictMode's double-invoke + remount problem entirely. Runs at most once
// per token (guarded via sessionStorage).
(function bootstrapGuestMagicLink() {
  try {
    if (!window.location.pathname.startsWith("/guests")) return;
    const url = new URL(window.location.href);
    const magic = url.searchParams.get("t");
    if (!magic) return;
    const guardKey = "ithaca_magic_consumed:" + magic;
    if (sessionStorage.getItem(guardKey)) return;
    sessionStorage.setItem(guardKey, "1");
    // Halt React boot until we've either logged in or given up.
    const rootEl = document.getElementById("root");
    if (rootEl) rootEl.setAttribute("data-guest-bootstrap", "1");
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    fetch(base + "/api/guests/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: magic }),
    })
      .then(r => r.json().then(j => ({ ok: r.ok, body: j })))
      .then(({ ok, body }) => {
        if (ok && body?.token) {
          localStorage.setItem("ithaca_guest_token", body.token);
          localStorage.setItem("ithaca_guest_email", body.email);
        } else {
          sessionStorage.removeItem(guardKey);
          if (body?.detail) sessionStorage.setItem("ithaca_magic_error", body.detail);
        }
      })
      .catch(() => { sessionStorage.removeItem(guardKey); })
      .finally(() => {
        // Regardless of outcome, land on a clean URL and let React render.
        window.location.replace("/guests");
      });
    // Prevent React from mounting while we're mid-exchange.
    // eslint-disable-next-line no-throw-literal
    throw "guest-bootstrap-in-progress";
  } catch (e) {
    if (e === "guest-bootstrap-in-progress") {
      // Halt module evaluation until location.replace fires.
      // The `throw` above bubbles out and skips ReactDOM.createRoot below.
      // We rely on window.location.replace() to reload with a clean URL,
      // at which point this block is a no-op (no `?t=` present).
      // Rethrow so the outer script terminates cleanly.
      throw e;
    }
  }
})();

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
